// Language-aware versions of the display helpers in src/lib/format.js. Parsing and ordering
// still come from lib/format; only the labels change. Messages sent to the admin keep using
// the Serbian lib/format.unitSummary so the office always reads the same text.
import { floorInfo, unitStatus } from "../lib/format";
import { LANGS, translate, useI18n } from ".";

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

const toNumber = (value) => {
  if (typeof value === "number") return value;
  const n = parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export function createFormat(lang) {
  const t = (key, vars) => translate(lang, key, vars);
  const locale = LANGS[lang].numberLocale;

  // Keys like "2.5" contain a dot, so read the maps whole instead of via dotted keys.
  const strukturaMap = t("unit.struktura");
  const strukturaPluralMap = t("unit.strukturaPlural");

  const strukturaLabel = (value) => {
    const n = toNumber(value);
    if (n === null) return value ? String(value) : "";
    return strukturaMap[String(n)] || t("unit.rooms", { count: String(n).replace(".", lang === "en" ? "." : ",") });
  };

  const strukturaPlural = (value) => {
    const n = toNumber(value);
    return strukturaPluralMap[String(n)] || strukturaLabel(value).toLowerCase();
  };

  // Serbian/British count floors above the ground floor; Russian counts the ground floor as 1.
  const floorNumber = (n) => {
    if (lang === "sr") return ROMAN[n] || String(n);
    if (lang === "ru") return String(n + 1);
    return String(n);
  };

  const floorLabel = (value) => {
    const info = floorInfo(value);
    if (!info.label) return "";
    const base = Math.floor(info.order);
    const attic = info.order % 1 !== 0 || info.order === 60;
    const plain = (n) => {
      if (n === -1) return t("unit.floors.-1");
      if (n === 0) return t("unit.floors.0");
      if (n > 0 && n < 50) return t("unit.floors.n", { n: floorNumber(n), roman: floorNumber(n) });
      return null;
    };
    if (info.order === 60) return t("unit.floors.attic");
    if (attic) return t("unit.floors.atticOf", { floor: lang === "sr" ? floorNumber(base) : plain(base) });
    return plain(base) ?? info.label;
  };

  const areaLabel = (value) => {
    const n = toNumber(value);
    return n === null ? "" : `${n.toLocaleString(locale, { maximumFractionDigits: 2 })} m²`;
  };

  const priceLabel = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const n = toNumber(value);
    if (n !== null && String(value).trim().match(/^[\d.,\s]+$/)) {
      return `${n.toLocaleString(locale, { maximumFractionDigits: 0 })} €`;
    }
    return String(value);
  };

  const unitTypeLabel = (tip) => {
    const key = String(tip || "stan").toLowerCase();
    return t("unit.types")[key] || tip;
  };

  const unitTitle = (unit) => `${unitTypeLabel(unit.tip)} ${unit.broj}`.trim();

  const statusLabel = (unit) => t(`unit.status.${unitStatus(unit)}`);

  const unitSummary = (unit, project) =>
    [
      project?.identifier || (unit.lamela ? `Lamela ${unit.lamela}` : ""),
      unitTitle(unit),
      floorLabel(unit.sprat),
      areaLabel(unit.povrsina),
      unit.tip === "stan" || !unit.tip ? strukturaLabel(unit.struktura) : "",
    ]
      .filter(Boolean)
      .join(" · ");

  return { strukturaLabel, strukturaPlural, floorLabel, areaLabel, priceLabel, unitTypeLabel, unitTitle, statusLabel, unitSummary };
}

const cache = {};
export const formatFor = (lang) => (cache[lang] ||= createFormat(lang));
export const useFormat = () => formatFor(useI18n().lang);
